import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { ScreenContext } from "@foundation/screen";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { useIntl } from "react-intl";
import styles from "./styles.module.css";
import MobileSparklineCards from "~components/TodayInMarkets/TodayInMarketsCategories/MobileSparklineCards";
import TabletSparklineCards from "~components/TodayInMarkets/TodayInMarketsCategories/TabletSparklineCards";
import DesktopSparklineCards from "~components/TodayInMarkets/TodayInMarketsCategories/DesktopSparklineCards";
import { useSectionFrontData } from "~hooks/SectionFront/useSectionFrontData";
import {
  deriveTodayInMarketsTabs,
  type MarketCategoryKey,
} from "~utils/todayInMarketsUtils";
import { useTodayInMarkets } from "~context/TodayInMarketsContext";
import { usePageData } from "~context/PageContext";
import { MARKETS_TAB_STORAGE_KEY } from "~constants/pagePreference";
import { addPageAction } from "~utils/newrelic/newrelicBrowser";

type TodayInMarketsCategoriesProps = {
  sectionFrontName: string;
  sectionFrontTabIdentifier: string;
};

export function TodayInMarketsCategories(props: TodayInMarketsCategoriesProps) {
  const { sectionFrontName, sectionFrontTabIdentifier } = props;

  const { locale } = useIntl();
  const { pagePreference, updatePagePreference } = usePageData();

  const {
    breakpoints: { tablet, smallDesktop, largeDesktop },
  } = useContext(ScreenContext);

  const { updateCategories, selectedCategory, onCategoryChange, categories } =
    useTodayInMarkets();

  // refresh sections market data in intervals
  const { data: sections } = useSectionFrontData(
    sectionFrontName,
    sectionFrontTabIdentifier,
    locale
  );

  const MARKETS_TAB_STORAGE_KEY_BY_SECTION_FRONT = useMemo(
    () => `${sectionFrontName}:${MARKETS_TAB_STORAGE_KEY}`,
    [sectionFrontName]
  );

  const selectedTab = useMemo(() => {
    return (
      (pagePreference[MARKETS_TAB_STORAGE_KEY_BY_SECTION_FRONT] as string) ||
      selectedCategory.id
    );
  }, [pagePreference, selectedCategory.id]);

  useEffect(() => {
    onCategoryChange(selectedTab);
  }, [onCategoryChange, selectedTab]);

  const onChange = useCallback(
    (categoryId: string) => {
      // Get the default english category label text used for tracking
      // Perhaps we should use the categoryId in the future. But for now use the legacy text
      const labelMsg =
        categories.find((c) => c.id === categoryId)?.label.defaultMessage ??
        categoryId;
      const eventLabel = `${labelMsg?.toLowerCase().replace(/ /g, "_")}_tim`;

      trackClickEvent("Market_Data", eventLabel, "click", "Market Data");

      updatePagePreference(
        MARKETS_TAB_STORAGE_KEY_BY_SECTION_FRONT,
        categoryId
      );

      onCategoryChange(categoryId);
      addPageAction("todayInMarketsTabChange", {
        tabName: labelMsg,
      });
    },
    [onCategoryChange]
  );

  // recompute Today In Market categories on new section front data
  useEffect(() => {
    if (sections?.length) {
      const derivedCategories = deriveTodayInMarketsTabs(
        sections,
        sectionFrontName as MarketCategoryKey
      );

      // refresh context with updated categories data
      updateCategories(derivedCategories);
    }
  }, [updateCategories, sections, sectionFrontName]);

  const isMobile = useMemo(
    () => !tablet && !smallDesktop && !largeDesktop,
    [tablet, smallDesktop, largeDesktop]
  );

  const isDesktop = useMemo(
    () => smallDesktop || largeDesktop,
    [smallDesktop, largeDesktop]
  );

  if (isMobile) {
    return (
      <div className={styles.todayInMarketsCategoriesContainer}>
        <MobileSparklineCards
          onChange={onChange}
          selectedOption={selectedTab}
        />
      </div>
    );
  }

  if (isDesktop) {
    return (
      <div className={styles.todayInMarketsCategoriesContainer}>
        <DesktopSparklineCards
          onChange={onChange}
          selectedOption={selectedTab}
        />
      </div>
    );
  }

  return (
    <div className={styles.todayInMarketsCategoriesContainer}>
      <TabletSparklineCards onChange={onChange} selectedOption={selectedTab} />
    </div>
  );
}
